import type { AggregatorProvider } from './supabase/fooddb';

/**
 * Canonical metric concepts the UI cares about. Each maps to (category, metric)
 * per provider — Swiggy and Zomato use different labels in flax_metrics_daily.
 *
 * Example:
 *  - Swiggy:  category="CONVERSION_FUNNEL" metric="Menu Opens"
 *  - Zomato:  category="Customer funnel"   metric="Menu opens"
 *
 * Adding a new metric? Add to MetricConcept union + the map below.
 */
export type MetricConcept =
  // funnel
  | 'funnel.impressions'
  | 'funnel.menu_opens'
  | 'funnel.carts_built'
  | 'funnel.orders_placed'
  // sales
  | 'sales.net_sales'
  | 'sales.delivered_orders'
  // ratings
  | 'ratings.poor_rated'
  | 'ratings.total_rated'
  // ads (Zomato has a single Ads category; Swiggy splits CPC + CBA)
  | 'ads.spend'
  | 'ads.roas'
  | 'ads.orders'
  // discounts
  | 'discount.restaurant_funded' // ₹ — restaurant-funded discount given
  | 'discount.pct_of_gmv'        // % — stored daily; compute at period level
  | 'discount.discounted_orders_pct' // % of orders that used a discount
  | 'discount.per_order'         // ₹/order, restaurant-funded
  | 'offers.orders_with_offer'   // count of orders that used an offer
  | 'offers.gmv_from_offers';    // ₹ — gross sales attributed to offers

interface Slot {
  category: string;
  metric: string;
}

type Map = Partial<Record<MetricConcept, Slot[]>>;

const SWIGGY: Map = {
  'funnel.impressions':    [{ category: 'CONVERSION_FUNNEL', metric: 'Impressions' }],
  'funnel.menu_opens':     [{ category: 'CONVERSION_FUNNEL', metric: 'Menu Opens' }],
  'funnel.carts_built':    [{ category: 'CONVERSION_FUNNEL', metric: 'Carts Builds' }],
  'funnel.orders_placed':  [{ category: 'CONVERSION_FUNNEL', metric: 'Orders Placed' }],
  'sales.net_sales':       [{ category: 'SALES',             metric: 'Net Sales' }],
  'sales.delivered_orders':[{ category: 'SALES',             metric: 'Delivered Orders' }],
  'ratings.poor_rated':    [{ category: 'RATINGS',           metric: 'Poor Rated Orders' }],
  'ratings.total_rated':   [{ category: 'RATINGS',           metric: 'total rated orders' }],
  // Swiggy splits ads into CPC + CBA — sum both for a unified total.
  'ads.spend':  [
    { category: 'ADS_CPC', metric: 'Total CPC Spends' },
    { category: 'ADS_CBA', metric: 'Total CBA Spends' },
  ],
  'ads.orders': [
    { category: 'ADS_CPC', metric: 'CPC Orders' },
    { category: 'ADS_CBA', metric: 'CBA Orders' },
  ],
  'ads.roas':   [{ category: 'ADS_CPC', metric: 'ROAS' }],
  // Swiggy's DISCOUNTS category — restaurant-funded only.
  'discount.restaurant_funded':    [{ category: 'DISCOUNTS', metric: 'Discount Given by Restaurant(RDGMV)' }],
  'discount.pct_of_gmv':           [{ category: 'DISCOUNTS', metric: 'Discount GMV %' }],
  'discount.discounted_orders_pct':[{ category: 'DISCOUNTS', metric: 'Discounted Orders %' }],
  'discount.per_order':            [{ category: 'DISCOUNTS', metric: 'Restaurant Discount Per Order(RDPO)' }],
};

const ZOMATO: Map = {
  'funnel.impressions':     [{ category: 'Customer funnel', metric: 'Impressions' }],
  'funnel.menu_opens':      [{ category: 'Customer funnel', metric: 'Menu opens' }],
  'funnel.carts_built':     [{ category: 'Customer funnel', metric: 'Cart builds' }],
  'funnel.orders_placed':   [{ category: 'Customer funnel', metric: 'Placed Orders' }],
  'sales.net_sales':        [{ category: 'Sales',           metric: 'Sales (Rs)' }],
  'sales.delivered_orders': [{ category: 'Sales',           metric: 'Delivered orders' }],
  'ratings.poor_rated':     [{ category: 'Customer experience', metric: 'Poor rated orders' }],
  'ratings.total_rated':    [{ category: 'Customer experience', metric: 'Rated orders' }],
  'ads.spend':              [{ category: 'Ads',             metric: 'Ads spend (Rs)' }],
  'ads.orders':             [{ category: 'Ads',             metric: 'Ads orders' }],
  'ads.roas':               [{ category: 'Ads',             metric: 'Ads ROI' }],
  // Zomato's Offers category — "Discount given (Rs)" is restaurant-funded
  // discount value; "Orders with offers" + "Gross sales from offers (Rs)"
  // describe offer take-up.
  'discount.restaurant_funded':    [{ category: 'Offers',          metric: 'Discount given (Rs)' }],
  'discount.pct_of_gmv':           [{ category: 'Offers',          metric: 'Effective discount (%)' }],
  'offers.orders_with_offer':      [{ category: 'Offers',          metric: 'Orders with offers' }],
  'offers.gmv_from_offers':        [{ category: 'Offers',          metric: 'Gross sales from offers (Rs)' }],
};

const MAP: Record<AggregatorProvider, Map> = {
  swiggy: SWIGGY,
  zomato: ZOMATO,
};

/** Returns the (category, metric) slots to query for a concept on a provider. */
export function slotsFor(provider: AggregatorProvider, concept: MetricConcept): Slot[] {
  return MAP[provider][concept] ?? [];
}
